import moment from 'moment';
import { NetworkService } from '../../services';
import { DiagnosticsConstants } from './types';
import { UtilityActions } from '../utility';
import { NetworkActions } from '../network';
import { config } from '../../config';

// API endpoints

const { BASE_URL } = config;

export class DiagnosticsActions {
  static loadDiagnosticData(api_location, data_api_location, action, dataPointsList) {
    return async (dispatch) => {
      const requestInfo = {
        category: 'diagnostics',
        api_location,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));
      await dispatch(DiagnosticsActions.getDiagnosticsRegistries(api_location));
      return dispatch(DiagnosticsActions.getDiagnosticsData(data_api_location, action, dataPointsList));
    };
  }

  static getDiagnosticsRegistries(api_location) {
    return async (dispatch, getState) => {
      dispatch({ type: DiagnosticsConstants.RequestDiagnosticsRegistries });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        return fetch(`${BASE_URL}${api_location}`, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch(DiagnosticsActions.setDiagnosticsError());
      }

      // Check the new state variable for the previous request
      const data = await response.json();
      const { data_points } = data;
      const newState = getState();
      const { lastRequest } = newState.networkReducer;
      // Make sure we are only handling correct responses
      if (lastRequest.api_location === api_location || lastRequest.api_location === undefined) {
        await dispatch({ type: DiagnosticsConstants.ReceiveDiagnosticsRegistries, payload: data_points });
      }
    };
  }

  static getDiagnosticsData(api_location, action = 'GET', dataPointsList = []) {
    return async (dispatch, getState) => {
      dispatch({ type: DiagnosticsConstants.RequestDiagnosticsOverview });

      const state = getState();
      const { graphDateRange, dataPointsList: dataPointsFromReducer } = state.diagnosticsReducer;
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        let API_ENDPOINT = `${BASE_URL}${api_location}?data_points=`;
        let filteredDataPointList = [];

        dataPointsList.map((dataPoint) => {
          return dataPointsFromReducer.map((dataPointObj) => {
            if (dataPointObj.tag === dataPoint) {
              return filteredDataPointList.push(dataPoint);
            }
          });
        });

        if (filteredDataPointList.length > 0) {
          API_ENDPOINT = `${API_ENDPOINT}${filteredDataPointList.join(',')}`;
        }
        if (graphDateRange) {
          const options = UtilityActions.getUtilityOptions(graphDateRange, 'Diagnostics');
          API_ENDPOINT = DiagnosticsActions.formatDiagnosticsUrl(API_ENDPOINT, options);
        }

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch(DiagnosticsActions.setDiagnosticsError());
      }

      // Check the new state variable for the previous request
      const data = await response.json();
      const { data_points } = data;
      const newState = getState();
      const { lastRequest } = newState.networkReducer;

      // // Make sure we are only handling correct responses
      if (lastRequest.subcategory === data.name.toLowerCase() || lastRequest.subcategory === undefined) {
        const lineColorObj = JSON.parse(window.sessionStorage.getItem('lineColorObj'));
        const availableColors = JSON.parse(window.sessionStorage.getItem('availableColors'));
        let isSessionStorageCorrect;

        if (action === 'GET' || dataPointsList.length === 0) {
          if (lineColorObj && Object.keys(lineColorObj).length !== 0) {
            isSessionStorageCorrect = DiagnosticsActions.areEqual(dataPointsList, Object.keys(lineColorObj));
          }
          if (isSessionStorageCorrect && lineColorObj && availableColors) {
            // used when refreshing the page to repopulate the reducer
            await dispatch(DiagnosticsActions.updateLineColors(lineColorObj, availableColors));
          } else {
            await dispatch(DiagnosticsActions.setLineColorsObj(data_points));
          }
        }
        await dispatch(DiagnosticsActions.updateSelectedDataPointsList());

        if (action === 'GET') {
          return dispatch({ type: DiagnosticsConstants.ReceiveDiagnosticsOverview, payload: data });
        } else if (action === 'UPDATE') {
          return dispatch({ type: DiagnosticsConstants.UpdateDiagnosticsDataRegistries, payload: data_points });
        }
      }
    };
  }

  static getCSVData() {
    return async (dispatch, getState) => {
      const callback = () => {
        const state = getState();
        const { graphDateRange, selectedDataPointsList } = state.diagnosticsReducer;
        const {
          selectedBuilding,
          navigationParams: {
            selectedSubCategory: { id: category_slug },
          },
        } = state.buildingReducer;
        let tagsList = [];
        selectedDataPointsList.map((dataPoint) => tagsList.push(dataPoint.tag));

        const options = UtilityActions.getUtilityOptions(graphDateRange, 'Diagnostics');

        const diff = moment(options.endDatetime).diff(moment(options.startDatetime), 'days', true);
        const interval = diff > 31 ? 'hour' : 'fifteen';

        const body = JSON.stringify({
          identifier: selectedBuilding.identifier,
          category_slug: category_slug,
          start_datetime: options.startDatetime,
          end_datetime: options.endDatetime,
          data_interval: interval,
          tags: tagsList,
        });
        const requestOptions = NetworkService.getRequestOptions('POST', body);
        let API_ENDPOINT = `${BASE_URL}/building/api/diagnostic/categories/${selectedBuilding.identifier}/${category_slug}/data/export/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return null;
      }

      const data = await response.text();
      return data;
    };
  }

  static areEqual(array1 = [], array2 = []) {
    // compare two arrays as sorted strings
    if (array1.sort().join(',') === array2.sort().join(',')) {
      return true;
    }
    return false;
  }

  static removeDiagnosticsData(diagnosticsData, dataList) {
    return async (dispatch) => {
      const newData = diagnosticsData.registries.filter((el) => dataList.indexOf(el.id.toString()) === -1);
      return dispatch({ type: DiagnosticsConstants.RemoveDiagnosticsDataRegistries, payload: newData });
    };
  }

  static updateDiagnosticsGraphDateRange(graphDateRange, customDataPoints) {
    return async (dispatch) => {
      window.sessionStorage.setItem('diagnosticsGraphDateRange', JSON.stringify(graphDateRange));
      await dispatch({ type: DiagnosticsConstants.UpdateGraphDateRange, payload: graphDateRange });
      return dispatch({ type: DiagnosticsConstants.SetCustomDataPointsList, payload: customDataPoints });
    };
  }

  // On initial load this function sets:
  // - the selected registers list
  // - the register id's mapped to colors
  // - the available colours
  static setLineColorsObj(registers) {
    const availableColors = ['line0', 'line1', 'line2', 'line3', 'line4', 'line5', 'line6'];
    const tmpColorObj = {};

    registers.map((register) => {
      tmpColorObj[register.id] = availableColors.shift();
      return tmpColorObj;
    });

    window.sessionStorage.setItem('lineColorObj', JSON.stringify(tmpColorObj));
    window.sessionStorage.setItem('availableColors', JSON.stringify(availableColors));

    return (dispatch) => {
      dispatch({ type: DiagnosticsConstants.UpdateLineColorsObj, payload: tmpColorObj });
      return dispatch({ type: DiagnosticsConstants.UpdateAvailableColors, payload: availableColors });
    };
  }

  static updateLineColors(lineColorObj, availableColors) {
    window.sessionStorage.setItem('lineColorObj', JSON.stringify(lineColorObj));
    window.sessionStorage.setItem('availableColors', JSON.stringify(availableColors));

    return (dispatch) => {
      dispatch({ type: DiagnosticsConstants.UpdateLineColorsObj, payload: lineColorObj });
      return dispatch({ type: DiagnosticsConstants.UpdateAvailableColors, payload: availableColors });
    };
  }

  static formatDiagnosticsUrl(url, { startDatetime, endDatetime, reportUnit }) {
    let urlFormatted = url;
    urlFormatted = `${urlFormatted}&report_unit=${reportUnit}`;
    urlFormatted = `${urlFormatted}&end_datetime=${endDatetime}`;
    urlFormatted = `${urlFormatted}&start_datetime=${startDatetime}`;
    return urlFormatted;
  }

  static updateSelectedDataPointsList() {
    return (dispatch, getState) => {
      const lineColorObj = JSON.parse(window.sessionStorage.getItem('lineColorObj'));
      let selectedDataPointIDs = [];
      try {
        selectedDataPointIDs = Object.keys(lineColorObj);
      } catch (e) {
        return null;
      }

      const state = getState();
      const { dataPointsList } = state.diagnosticsReducer;
      if (dataPointsList) {
        const selectedDataPointList = dataPointsList.filter((dataPoint) =>
          selectedDataPointIDs.includes(dataPoint.tag),
        );
        return dispatch({ type: DiagnosticsConstants.UpdateSelectedDataPointsList, payload: selectedDataPointList });
      }
    };
  }

  static pollDiagnostics(building, subcategory, timestamp, startDatetime, endDatetime) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        let urlFormatted = `${BASE_URL}/building/api/diagnostic/categories/${building}/${subcategory}/data-poll/?timestamp=${timestamp}&end_datetime=${endDatetime}&start_datetime=${startDatetime}`;

        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      return data.update_data;
    };
  }

  static updateDataPointName(value, updateApiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const requestBody = JSON.stringify({
          display_name: value,
        });
        const requestOptions = NetworkService.getRequestOptions('PATCH', requestBody);
        let API_ENDPOINT = `${BASE_URL}${updateApiLocation}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);

      if (!response.ok) {
        return null;
      }

      return true;
    };
  }

  static setDiagnosticsError() {
    return { type: DiagnosticsConstants.SetDiagnosticsError };
  }

  static clearDiagnosticsError() {
    return { type: DiagnosticsConstants.ClearDiagnosticsError };
  }
}
